import { FloatingDelayGroup } from '@floating-ui/react';
import { Pill, ShowMore, Tooltip, Typography } from '@recruit-robin/robin-theme/web-components';
import { CheckCircle, ChevronDown, ChevronUp, FileAttachment, XCircle } from '@recruit-robin/robin-theme/web-icons';
import { memo } from 'react';

import { DateFormat, formatDate, formatDateLong } from '@/shared/lib/date';
import { useDictionary } from '@/shared/lib/i18n';
import { Ellipse } from '@/shared/ui/ellipse';
import { ActivityLogAction, CandidateViewModel, HistoryViewModel } from '../../model';
import { getActivityIcon, getActivityLabel } from '../activity-log-preview';
import { Header } from './header';
import { useStyles } from './HistoryCard.styles';

export const HistoryCard = memo(
  ({
    history,
    actions,
    candidate,
  }: {
    history: HistoryViewModel;
    actions: React.ReactNode;
    candidate: CandidateViewModel;
  }) => {
    const dictionary = useDictionary();
    const classes = useStyles();

    const activityIcon = getActivityIcon({
      size: 24,
      color: (c) => (history.action === ActivityLogAction.Hidden ? c.tertiary.lime[50] : c.primary.fuchsia[40]),

      action: history.action,
      source: history.candidateNotification.sendMethod,
    });
    const activityLabel = getActivityLabel({
      action: history.action,
      source: history.candidateNotification.sendMethod,
    });

    return (
      <div css={classes.root}>
        <Header
          icon={activityIcon}
          title={
            <Header.Title>
              <Typography customCss={classes.title} variant={(v) => v.heading[6]} color={(c) => c.neutral.variant[90]}>
                {activityLabel}
              </Typography>

              <Header.TitleDetails>
                <FloatingDelayGroup delay={{ open: 500, close: 0 }}>
                  <Tooltip label={formatDateLong(history.date)} zIndex={3} placement="top" offset={{ mainAxis: 8 }}>
                    <Typography variant={(v) => v.supporting['label-tiny']} color={(c) => c.neutral.variant[60]} inline>
                      {formatDate(history.date, { format: DateFormat['01 Jan 2026'] })}
                    </Typography>
                  </Tooltip>
                </FloatingDelayGroup>

                {actions && <Header.TitleDetailsButton options={actions} />}
              </Header.TitleDetails>
            </Header.Title>
          }
          subtitle={
            <Header.Subtitle>
              <Typography variant={(v) => v.supporting['label-tiny']} color={(c) => c.neutral.variant[50]} inline>
                {history.author}
              </Typography>

              <Ellipse color={(c) => c.neutral.variant[60]} />

              <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[90]} inline>
                {history.campaign.name}
              </Typography>
            </Header.Subtitle>
          }
        />

        {history.action === ActivityLogAction.RJCommHubCandidateAccepted && (
          <>
            <Pill
              customCss={classes.pill}
              style="icon-removable"
              type="success"
              label={<Typography variant={(v) => v.supporting['label-tiny']}>{dictionary.interested}</Typography>}
              startIcon={CheckCircle}
            />
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[90]}>
              {dictionary.goodNewsCandidate(candidate.name, history.jobTitle || history.campaign.name)}
            </Typography>
            {history.jobTitle && (
              <Pill
                customCss={classes.pill}
                style="filter-tag"
                type="subtle1"
                label={<Typography variant={(v) => v.supporting.label}>{history.jobTitle}</Typography>}
                startIcon={FileAttachment}
              />
            )}
          </>
        )}

        {history.action === ActivityLogAction.RJCommHubCandidateRejected && (
          <>
            <Pill
              customCss={classes.pill}
              style="icon-removable"
              type="error"
              label={<Typography variant={(v) => v.supporting['label-tiny']}>{dictionary.notInterested}</Typography>}
              startIcon={XCircle}
            />
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[90]}>
              {dictionary.candidateNotInterested(candidate.name, history.jobTitle || history.campaign.name)}
            </Typography>
          </>
        )}

        {history.message.sanitizedBody && (
          <ShowMore.Root>
            <ShowMore.Content
              rootCustomCss={classes.showMoreRoot}
              variant={(v) => v.body.short}
              transition={{ duration: 0.22 }}>
              <div css={classes.message}>
                {history.message.sanitizedSubject && (
                  <>
                    <span>
                      <Typography variant={(v) => v.heading[6]} color={(c) => c.neutral.variant[90]} inline>
                        {dictionary.subject}:
                      </Typography>
                      &nbsp;
                      <span
                        css={classes.messageSubject}
                        dangerouslySetInnerHTML={{ __html: history.message.sanitizedSubject }}
                      />
                    </span>

                    <br />
                    <br />
                  </>
                )}
                <span css={classes.messageBody} dangerouslySetInnerHTML={{ __html: history.message.sanitizedBody }} />
                {history.message.sanitizedSignature && (
                  <span
                    css={classes.messageSignature}
                    dangerouslySetInnerHTML={{ __html: history.message.sanitizedSignature }}
                  />
                )}
              </div>
            </ShowMore.Content>
            <ShowMore.Less customCss={classes.showMoreContainer}>
              <Typography
                customCss={classes.showMoreButton}
                variant={(v) => v.supporting.helper}
                color={(c) => c.primary.fuchsia[60]}>
                <ChevronUp size={16} />
                {dictionary.read}
              </Typography>
            </ShowMore.Less>
            <ShowMore.More customCss={classes.showMoreContainer}>
              <Typography
                customCss={classes.showMoreButton}
                variant={(v) => v.supporting.helper}
                color={(c) => c.primary.fuchsia[60]}>
                <ChevronDown size={16} />
                {dictionary.read}
              </Typography>
            </ShowMore.More>
          </ShowMore.Root>
        )}
      </div>
    );
  },
  (prev, next) => prev.history === next.history,
);
